import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { ChannelManagerText, channelManagerText } from '../i18n/pages/channelManager';
import {
  HostexProperty,
  PmsConnection,
  PmsLinkPreview,
  getHostexConnection,
  getHostexProperties,
  linkApartment,
  previewApartmentLink,
  setSyncPastReservations,
  unlinkApartment,
} from '../api.channelManager';
import { ApartmentCalendar, syncCalendar } from '../api.calendar';
import HostexSetupGuide from './HostexSetupGuide';
import AttentionReservationList from './AttentionReservationList';
import DuplicateReservationList from './DuplicateReservationList';

interface Props {
  apartmentId: number;
  /**
   * The apartment's calendars, fetched once by the parent (CalendarManager). This component
   * derives its linked source from this prop instead of fetching its own copy on mount — only
   * `onSourcesChanged` triggers a network call, and only after an action that can change the list.
   */
  calendars: ApartmentCalendar[];
  /** Refetches the apartment's calendars in the parent and returns the fresh list. */
  onSourcesChanged: () => Promise<ApartmentCalendar[]>;
}

const SYNC_COOLDOWN_MS = 60_000;

type Tone = 'ok' | 'warn' | 'error';

/**
 * Maps one apartment to a property on the manager's channel-manager account.
 *
 * The account-level connection lives in Manager profile, because one provider token covers every
 * property; this is only the per-apartment half. When no connection exists there is nothing
 * useful to do here, so the section says so and points at the place that fixes it.
 *
 * Once an apartment is linked, this leads with a status card — what it's linked to, whether it's
 * healthy — rather than the editable controls that got it there. Re-mapping the property, the
 * past-sync toggle and Unlink all move behind an explicit "Manage settings" row, closed by
 * default and never remembered open: a live integration should not be one habitual click away
 * from being changed.
 */
const ChannelManagerSection: React.FC<Props> = ({ apartmentId, calendars, onSourcesChanged }) => {
  const { lang } = useLang();
  const t = channelManagerText[lang];

  const [connection, setConnection] = useState<PmsConnection | null>(null);
  const [properties, setProperties] = useState<HostexProperty[]>([]);
  const [apiSource, setApiSource] = useState<ApartmentCalendar | null>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [syncPast, setSyncPast] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncCooldownUntil, setSyncCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  const [preview, setPreview] = useState<PmsLinkPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [attentionKey, setAttentionKey] = useState(0);

  // Deliberately not persisted: a manager who habitually keeps this open loses the guard rail it
  // exists for. It starts closed every time the panel is opened.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);

  // This component's own data — the calendar list belongs to the parent (see `calendars` prop),
  // so this never fetches it itself on mount, which is what caused the duplicate request.
  const loadConnection = useCallback(async () => {
    setLoading(true);
    try {
      const conn = await getHostexConnection();
      setConnection(conn);

      if (conn.connected && conn.enabled) {
        setLoadingProperties(true);
        try {
          setProperties(await getHostexProperties());
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to load properties');
        } finally {
          setLoadingProperties(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load channel manager settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  // Recomputes the linked source whenever the parent's calendar list changes — on first arrival,
  // and again after link/unlink/sync refreshes it via onSourcesChanged.
  useEffect(() => {
    const existing = calendars.find((c) => c.connectionType === 'PMS_API') || null;
    setApiSource(existing);
    setSelectedPropertyId(existing?.externalPropertyId || '');
    setSyncPast(existing?.syncPastReservations ?? true);
  }, [calendars]);

  // Ticks only while a cooldown is running, then stops.
  useEffect(() => {
    if (syncCooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [syncCooldownUntil]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  /**
   * Asks the server what linking would do before doing it. When feeds exist the answer is a
   * confirmation dialog; when they do not, the switch is harmless and happens straight away.
   */
  const handleLinkClick = async () => {
    clearMessages();
    if (!selectedPropertyId) return;
    try {
      const p = await previewApartmentLink(apartmentId);
      if (p.requiresConfirmation) {
        setPreview(p);
      } else {
        await performLink(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to prepare the switch');
    }
  };

  const performLink = async (confirmed: boolean) => {
    const property = properties.find((p) => String(p.id) === selectedPropertyId);
    setLinking(true);
    clearMessages();
    try {
      await linkApartment(apartmentId, selectedPropertyId, property?.title || '', confirmed, syncPast);
      setPreview(null);
      setSuccess(t.linkSuccess);
      // Only the calendar list changed — connection health doesn't move when an apartment links.
      await onSourcesChanged();
      setAttentionKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to link');
    } finally {
      setLinking(false);
    }
  };

  /**
   * Before linking, this only holds the value for the link request that follows. Once linked, an
   * apartment already has a source row to flip the flag on, so the change takes effect right away
   * rather than waiting for a "change property" click.
   */
  const handleTogglePastSync = async (checked: boolean) => {
    setSyncPast(checked);
    if (!apiSource) return;
    clearMessages();
    try {
      await setSyncPastReservations(apartmentId, checked);
      setSuccess(checked ? t.syncPastReservationsOnSuccess : t.syncPastReservationsOffSuccess);
    } catch (err: any) {
      setSyncPast(!checked);
      setError(err.response?.data?.message || err.message || 'Failed to update');
    }
  };

  const handleUnlink = async () => {
    clearMessages();
    try {
      await unlinkApartment(apartmentId);
      setConfirmingUnlink(false);
      setSettingsOpen(false);
      setSuccess(t.unlinkSuccess);
      await onSourcesChanged();
      setAttentionKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to unlink');
    }
  };

  const handleSync = async () => {
    if (!apiSource) return;
    clearMessages();
    setSyncing(true);
    try {
      const result = await syncCalendar(apartmentId, apiSource.id);
      setSuccess(
        `${result.imported} new · ${result.updated} updated · ${result.cancelled} cancelled`
      );
      // A sync can move connection.lastPollAt as well as the calendar rows, so refresh both.
      await Promise.all([loadConnection(), onSourcesChanged()]);
      setAttentionKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncCooldownUntil(Date.now() + SYNC_COOLDOWN_MS);
    }
  };

  if (loading) {
    return <div style={{ color: '#718096', fontSize: '0.9rem' }}>…</div>;
  }

  // ── Not connected at all ──────────────────────────────────────────────────
  if (!connection || !connection.connected) {
    return (
      <div
        style={{
          padding: '16px 18px',
          background: '#f7fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
        }}
      >
        <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: 4 }}>{t.notConnectedTitle}</div>
        <div style={{ fontSize: '0.87rem', color: '#4a5568', marginBottom: 10 }}>
          {t.notConnectedDesc}
        </div>
        <Link
          to="/profile?tab=integrations"
          style={{ fontSize: '0.87rem', fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}
        >
          {t.goToProfile}
        </Link>
      </div>
    );
  }

  const cooling = syncCooldownUntil > now;
  const remaining = Math.max(0, Math.ceil((syncCooldownUntil - now) / 1000));

  // ── Connection health, folded into the status card once an apartment is linked ─────────────
  const tone: Tone = !connection.enabled
    ? 'error'
    : !connection.canWrite || !connection.hasWebhookSecret
      ? 'warn'
      : 'ok';

  const subLines: React.ReactNode[] = [];
  if (tone === 'error') {
    subLines.push(connection.lastError || t.disabledDesc);
  } else if (tone === 'warn') {
    if (!connection.canWrite) subLines.push(t.readOnlyWarning);
    if (!connection.hasWebhookSecret) {
      subLines.push(connection.secretEnrolmentOpen ? t.webhookWaitingSub : t.webhookExpiredSub);
      if (!connection.secretEnrolmentOpen) {
        subLines.push(
          <Link to="/profile?tab=integrations" style={{ color: 'inherit', fontWeight: 700 }}>
            {t.goToProfile}
          </Link>
        );
      }
    }
  } else {
    subLines.push(t.linkedHealthySub);
  }

  return (
    <div>
      {error && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: 14,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
            fontSize: '0.87rem',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: 14,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            color: '#166534',
            fontSize: '0.87rem',
          }}
        >
          {success}
        </div>
      )}

      {apiSource ? (
        <>
          <StatusCard
            tone={tone}
            pillLabel={
              tone === 'ok' ? t.statusPillConnected : tone === 'warn' ? t.statusPillActionNeeded : t.statusPillPaused
            }
            linkedToLabel={t.linkedTo}
            propertyName={apiSource.externalPropertyName || apiSource.externalPropertyId || '—'}
            subLines={subLines}
            meta={[
              { label: t.channelManagerLabel, value: providerDisplayName(connection.provider) },
              { label: t.lastEventAt, value: formatWhen(connection.lastWebhookAt, t.never) },
              { label: t.lastPollAt, value: formatWhen(connection.lastPollAt, t.never) },
              { label: t.metaPastLabel, value: syncPast ? t.metaPastIncluded : t.metaPastExcluded },
            ]}
            actions={
              <button
                type="button"
                className="app-btn app-btn-blue app-btn-compact"
                disabled={syncing || cooling}
                onClick={handleSync}
              >
                {cooling ? `${remaining}s` : t.syncNow}
              </button>
            }
          />

          <AttentionReservationList apartmentId={apartmentId} refreshKey={attentionKey} />

          <DuplicateReservationList
            apartmentId={apartmentId}
            refreshKey={attentionKey}
            onMerged={() => setAttentionKey((k) => k + 1)}
          />

          <button
            type="button"
            onClick={() => setSettingsOpen((o) => !o)}
            style={settingsToggleStyle}
            aria-expanded={settingsOpen}
          >
            <span>⚙️ {t.manageSettings}</span>
            <span
              style={{
                marginLeft: 'auto',
                color: '#a0aec0',
                fontSize: '0.75rem',
                transition: 'transform 0.15s ease',
                transform: settingsOpen ? 'rotate(90deg)' : 'none',
              }}
            >
              ▶
            </span>
          </button>

          {settingsOpen && (
            <div style={settingsPanelStyle}>
              <div style={{ marginBottom: 18 }}>
                <label style={fieldLabelStyle}>{t.propertyLabel}</label>
                {loadingProperties ? (
                  <div style={{ fontSize: '0.87rem', color: '#718096' }}>{t.loadingProperties}</div>
                ) : properties.length === 0 ? (
                  <div style={{ fontSize: '0.87rem', color: '#718096' }}>{t.noProperties}</div>
                ) : (
                  <select
                    value={selectedPropertyId}
                    onChange={(e) => setSelectedPropertyId(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">{t.propertyPlaceholder}</option>
                    {properties.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                )}
                <div style={{ fontSize: '0.78rem', color: '#a0aec0', marginTop: 6 }}>
                  {t.changePropertyHint}
                </div>
                {selectedPropertyId !== (apiSource.externalPropertyId || '') && selectedPropertyId && (
                  <button
                    type="button"
                    className="app-btn app-btn-green app-btn-compact"
                    style={{ marginTop: 10 }}
                    disabled={linking}
                    onClick={handleLinkClick}
                  >
                    {linking ? t.linking : t.changeProperty}
                  </button>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.85rem', color: '#2d3748' }}>
                <input
                  type="checkbox"
                  checked={syncPast}
                  onChange={(e) => handleTogglePastSync(e.target.checked)}
                />
                {t.syncPastReservationsLabel}
              </label>
              <div style={{ fontSize: '0.78rem', color: '#a0aec0', marginTop: 3, marginLeft: 22 }}>
                {t.syncPastReservationsHint}
              </div>

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #fca5a5' }}>
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#dc2626',
                    marginBottom: 8,
                  }}
                >
                  {t.dangerZoneLabel}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ fontSize: '0.83rem', color: '#4a5568', maxWidth: '40ch', lineHeight: 1.5 }}>
                    {t.unlinkDangerCopy}
                  </div>
                  <button
                    type="button"
                    className="app-btn app-btn-red app-btn-compact"
                    onClick={() => setConfirmingUnlink(true)}
                  >
                    {t.unlink}
                  </button>
                </div>
                {confirmingUnlink && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      padding: '10px 12px',
                      marginTop: 10,
                      background: '#fef2f2',
                      border: '1px solid #fca5a5',
                      borderRadius: 8,
                    }}
                  >
                    <span style={{ fontSize: '0.84rem', color: '#991b1b', fontWeight: 600 }}>
                      {t.unlinkConfirmPrompt}
                    </span>
                    <button
                      type="button"
                      className="app-btn app-btn-red app-btn-compact"
                      onClick={handleUnlink}
                    >
                      {t.unlinkConfirmYes}
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-blue app-btn-compact"
                      onClick={() => setConfirmingUnlink(false)}
                    >
                      {t.cancel}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={linkCardStyle}>
          {tone === 'error' && (
            <div
              style={{
                display: 'flex',
                gap: 9,
                alignItems: 'flex-start',
                padding: '9px 12px',
                marginBottom: 14,
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: 8,
                color: '#991b1b',
                fontSize: '0.84rem',
              }}
            >
              <span>⛔</span>
              <span>{connection.lastError || t.disabledDesc}</span>
            </div>
          )}

          <label style={fieldLabelStyle}>{t.propertyLabel}</label>
          {loadingProperties ? (
            <div style={{ fontSize: '0.87rem', color: '#718096' }}>{t.loadingProperties}</div>
          ) : properties.length === 0 ? (
            <div style={{ fontSize: '0.87rem', color: '#718096' }}>{t.noProperties}</div>
          ) : (
            <select
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              style={selectStyle}
            >
              <option value="">{t.propertyPlaceholder}</option>
              {properties.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.title}
                </option>
              ))}
            </select>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 14, fontSize: '0.87rem', color: '#2d3748' }}>
            <input
              type="checkbox"
              checked={syncPast}
              onChange={(e) => setSyncPast(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              {t.syncPastReservationsLabel}
              <span style={{ display: 'block', fontSize: '0.78rem', color: '#a0aec0', marginTop: 2 }}>
                {t.syncPastReservationsHint}
              </span>
            </span>
          </label>

          <div style={{ marginTop: 18 }}>
            <button
              type="button"
              className="app-btn app-btn-green app-btn-standard"
              disabled={!selectedPropertyId || linking}
              onClick={handleLinkClick}
            >
              {linking ? t.linking : t.confirmSwitch}
            </button>
          </div>
        </div>
      )}

      {/* Switch confirmation */}
      {preview && (
        <ConfirmSwitchDialog
          preview={preview}
          t={t}
          busy={linking}
          onCancel={() => setPreview(null)}
          onConfirm={() => performLink(true)}
        />
      )}

      <HostexSetupGuide />
    </div>
  );
};

// ── Small presentational helpers ───────────────────────────────────────────────

// Hostex is the only provider today, but the connection is provider-agnostic (see PmsProvider.java)
// so this stays a lookup rather than a hardcoded label. Extend it when a second one lands — an
// unmapped value still falls back to the raw provider string instead of disappearing.
const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  HOSTEX: 'Hostex',
};

const providerDisplayName = (provider: string): string => PROVIDER_DISPLAY_NAMES[provider] ?? provider;

const TONE_PALETTE: Record<Tone, { accent: string; soft: string; text: string; icon: string }> = {
  ok: { accent: '#16a34a', soft: '#f0fdf4', text: '#166534', icon: '✅' },
  warn: { accent: '#d97706', soft: '#fef9c3', text: '#713f12', icon: '⚠️' },
  error: { accent: '#dc2626', soft: '#fef2f2', text: '#991b1b', icon: '⛔' },
};

const StatusCard: React.FC<{
  tone: Tone;
  pillLabel: string;
  linkedToLabel: string;
  propertyName: string;
  subLines: React.ReactNode[];
  meta: { label: string; value: string }[];
  actions?: React.ReactNode;
}> = ({ tone, pillLabel, linkedToLabel, propertyName, subLines, meta, actions }) => {
  const palette = TONE_PALETTE[tone];

  return (
    <div
      style={{
        position: 'relative',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        padding: '18px 20px',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', inset: '0 auto 0 0', width: 4, background: palette.accent }} />
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          style={{
            flexShrink: 0,
            width: 38,
            height: 38,
            borderRadius: 10,
            background: palette.soft,
            color: palette.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.05rem',
          }}
        >
          {palette.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'inline-block',
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 999,
              background: palette.soft,
              color: palette.text,
              marginBottom: 6,
            }}
          >
            {pillLabel}
          </div>
          <div style={{ fontSize: '1.02rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.35 }}>
            {linkedToLabel} <span style={{ color: palette.accent }}>{propertyName}</span>
          </div>
          {subLines.map((line, i) => (
            <div key={i} style={{ fontSize: '0.84rem', color: '#4a5568', marginTop: i === 0 ? 5 : 3, lineHeight: 1.5 }}>
              {line}
            </div>
          ))}
        </div>
      </div>

      {meta.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 18,
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid #f1f5f9',
          }}
        >
          {meta.map((m) => (
            <div key={m.label} style={{ minWidth: 110 }}>
              <div
                style={{
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#a0aec0',
                  marginBottom: 2,
                }}
              >
                {m.label}
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2d3748' }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}

      {actions && <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
};

const ConfirmSwitchDialog: React.FC<{
  preview: PmsLinkPreview;
  t: ChannelManagerText;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ preview, t, busy, onCancel, onConfirm }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15,23,42,.45)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      zIndex: 1000,
    }}
    role="dialog"
    aria-modal="true"
  >
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        maxWidth: 560,
        width: '100%',
        padding: 24,
        boxShadow: '0 12px 40px rgba(0,0,0,.18)',
      }}
    >
      <h4 style={{ margin: '0 0 10px', fontSize: '1.05rem', fontWeight: 700 }}>{t.confirmTitle}</h4>
      <p style={{ fontSize: '0.9rem', color: '#4a5568', margin: '0 0 14px' }}>
        {t.confirmIntro(preview.icalFeedsToRemove.length)}
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
        <Row ok>{t.confirmKeepReservations}</Row>
        <Row ok>{t.confirmKeepForms}</Row>
        <Row ok>{t.confirmKeepPast}</Row>
        <Row>{t.confirmRemoveFeeds(preview.icalFeedsToRemove.join(', '))}</Row>
        {preview.reservationsDiscarded > 0 && (
          <Row>{t.confirmDiscard(preview.reservationsDiscarded)}</Row>
        )}
      </ul>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="app-btn app-btn-green app-btn-standard"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? t.linking : t.confirmSwitch}
        </button>
        <button
          type="button"
          className="app-btn app-btn-blue app-btn-standard"
          disabled={busy}
          onClick={onCancel}
        >
          {t.cancel}
        </button>
      </div>
    </div>
  </div>
);

const Row: React.FC<{ ok?: boolean; children: React.ReactNode }> = ({ ok, children }) => (
  <li style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: '0.87rem', padding: '6px 0' }}>
    <span style={{ color: ok ? '#38a169' : '#c53030', fontWeight: 700 }}>{ok ? '✓' : '✕'}</span>
    <span style={{ color: '#2d3748' }}>{children}</span>
  </li>
);

const formatWhen = (iso: string | null, never: string): string =>
  iso ? new Date(iso).toLocaleString() : never;

// ── Shared inline styles ────────────────────────────────────────────────────────

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: '#4a5568',
  marginBottom: 5,
};

const selectStyle: React.CSSProperties = {
  padding: '8px 11px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: '0.88rem',
  background: '#fff',
  color: '#2d3748',
  minWidth: 280,
  maxWidth: '100%',
};

const linkCardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1.5px dashed #cbd5e1',
  borderRadius: 14,
  padding: '20px 20px',
};

const settingsToggleStyle: React.CSSProperties = {
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  boxSizing: 'border-box',
  padding: '13px 16px',
  marginTop: 20,
  background: '#f7fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  cursor: 'pointer',
  fontSize: '0.87rem',
  fontWeight: 700,
  color: '#4a5568',
};

const settingsPanelStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderTop: 'none',
  borderRadius: '0 0 12px 12px',
  padding: '20px 18px 18px',
  marginTop: -1,
};

export default ChannelManagerSection;
